import React, { createContext, useState, useRef, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { giphyBlack } from '@giphy/js-brand'
import { Toggle } from '@giphy/react-giphy-brand'
import { IChannel } from 'shared/types/channel'
import BannerUpload from './banner-upload'
import Footer from './footer'
import OptionalLabel from './optional-label'
import { DescriptionField, Label, NameField, Section, DisableModal } from './sc'
import { NewCollectionProps } from './modal'
import { Item, ItemGif, ItemText, NewCollectionHeader, PrivateOverlay } from './add-to-collection/sc'
import { getWebp } from 'shared/util/gif-extract'
import { colors } from './add-to-collection/utils'
import { fetchChannel } from 'shared/api/channels'

const DEFAULT_GIF = 'https://media.giphy.com/media/fTytsfKCVyb6lB75jM/giphy.gif'
const PUBLIC_GRADIENT = `linear-gradient(-135deg, rgb(0, 255, 153) 0%, rgb(0, 204, 255) 100%)`
const PRIVATE_GRADIENT = `linear-gradient(45deg, rgb(255, 102, 102) 0%, rgb(230, 70, 182) 100%)`

const defaultChannel: Partial<IChannel> = {
    is_private: false,
}

const PrivacyToggle = styled(Toggle)`
    width: 100px;
    height: 36px;
    border-radius: 100px;
    position: absolute;
    right: 40px;
    top: 5.5px;
`

const Overlay = styled.div<{ background: string }>`
    background: ${props => props.background};
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: 0.3;
    border-radius: 6px 6px 0px 0px;
`

type CollectionEditorContextProps = {
    updateChannel: (channel: Partial<IChannel>) => void
    channel: Partial<IChannel>
    newCollectionProps?: NewCollectionProps
    onComplete?: () => void
    setDisabled: (disabled: boolean) => void
}
export const CollectionEditorContext = createContext({} as CollectionEditorContextProps)

type Props = {
    channel?: Partial<IChannel>
    newCollectionProps?: NewCollectionProps
    onComplete?: () => void
}

const CollectionEditor = ({ channel: currentChannel, newCollectionProps, onComplete }: Props) => {
    const isNewChannel = !!newCollectionProps
    const [channel, setChannel] = useState<Partial<IChannel>>(currentChannel || defaultChannel)
    const [isDisabled, setDisabled] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const updateChannel = (updateProps: Partial<IChannel>) => setChannel({ ...channel, ...updateProps })
    const value = {
        updateChannel,
        channel,
        newCollectionProps,
        onComplete,
        setDisabled,
    }
    const focusRef = useRef<HTMLInputElement>(null)

    const togglePrivacy = (isOn: boolean) => {
        updateChannel({ is_private: isOn })
    }

    const gifSrc = isNewChannel ? getWebp(newCollectionProps?.gif) : getWebp(channel?.featured_gif)

    const randomColor = useMemo(() => {
        return colors[Math.floor(Math.random() * colors.length)]
    }, [colors])

    const completeChannelInfo = async () => {
        if (!newCollectionProps && channel.id) {
            const updatedChannelInfo = await fetchChannel(channel.id)
            updateChannel(updatedChannelInfo)
        }
        setLoading(false)
    }

    useEffect(() => {
        focusRef.current?.focus()
        completeChannelInfo()
    }, [])

    if (loading) {
        return <></>
    }

    return (
        <CollectionEditorContext.Provider value={value}>
            {isNewChannel && (
                <PrivacyToggle
                    isOn={!channel.is_private}
                    width={100}
                    height={34}
                    toggle={togglePrivacy}
                    borderRadius={100}
                    onElement={<span className="ss-unlock"></span>}
                    offElement={<span className="ss-lock"></span>}
                    onColor={PUBLIC_GRADIENT}
                    offColor={PRIVATE_GRADIENT}
                    backgroundColor={giphyBlack}
                />
            )}
            {isNewChannel ? (
                <NewCollectionHeader>
                    <Item>
                        {!gifSrc && <Overlay background={randomColor} />}
                        <ItemGif src={gifSrc || DEFAULT_GIF} alt={newCollectionProps?.gif?.title} />
                        <ItemText background={randomColor}>New Collection</ItemText>
                    </Item>
                </NewCollectionHeader>
            ) : (
                <NewCollectionHeader>
                    <Item>
                        <ItemGif src={gifSrc || DEFAULT_GIF} alt={channel.featured_gif?.title} />
                        <ItemText background={randomColor}>{channel.display_name}</ItemText>
                        {channel.is_private && (
                            <PrivateOverlay>
                                <span className="ss-lock" />
                            </PrivateOverlay>
                        )}
                    </Item>
                </NewCollectionHeader>
            )}
            <Section>
                <Label>Name</Label>
                <NameField
                    ref={focusRef}
                    maxLength={50}
                    placeholder="Collection Name"
                    value={channel.display_name || ''}
                    onChange={({ target: { value } }) => {
                        updateChannel({ display_name: value })
                    }}
                ></NameField>
            </Section>
            {!isNewChannel && (
                <>
                    <Section>
                        <OptionalLabel label="Description" />
                        <DescriptionField
                            maxLength={200}
                            placeholder="Add a brief description about your collection."
                            as="textarea"
                            value={channel.description || ''}
                            onChange={({ target: { value } }) => {
                                updateChannel({ description: value })
                            }}
                        ></DescriptionField>
                    </Section>
                    <BannerUpload />
                </>
            )}
            <Footer />
            {isDisabled && <DisableModal />}
        </CollectionEditorContext.Provider>
    )
}
export default CollectionEditor
